import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogContent, DialogTitle, FormControl, Grid,
  InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AssessmentIcon   from '@mui/icons-material/Assessment';
import PersonIcon       from '@mui/icons-material/Person';
import CoffeeIcon       from '@mui/icons-material/Coffee';
import WarningIcon      from '@mui/icons-material/Warning';
import DownloadIcon     from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon   from '@mui/icons-material/TableChart';
import {
  getOperators, getLocations, getEvents,
  getAttendanceReport, getDrowsinessReport,
  getPresenceSummaries, getShiftUtilizationReport,
  isAdmin,
} from '../api/client';
import api from '../api/client';
import useDetectionSettings, { enabled } from '../hooks/useDetectionSettings';
import useTimezone from '../hooks/useTimezone';
import { formatDateTime, zonedStartOfDay, zonedEndOfDay } from '../utils/format';

const today    = () => new Date().toISOString().slice(0, 10);
const weekAgo  = () => { const d = new Date(); d.setDate(d.getDate() - 7);  return d.toISOString().slice(0, 10); };
const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); };

// ── Excel download helper ─────────────────────────────────────────────────────
function downloadExcel(rows, columns, filename) {
  // Build CSV (opens in Excel, LibreOffice Calc etc.)
  const header = columns.map(c => `"${c.label}"`).join(',');
  const body   = rows.map(r =>
    columns.map(c => {
      const v = c.value(r);
      return typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : (v ?? '');
    }).join(',')
  ).join('\n');
  const csv  = '\uFEFF' + header + '\n' + body; // BOM for Excel UTF-8
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── PDF download helper ────────────────────────────────────────────────────────
function downloadPDF(rows, columns, title, dateFrom, dateTo) {
  // Build a simple HTML page and print to PDF via browser print dialog
  const headerHtml = columns.map(c => `<th>${c.label}</th>`).join('');
  const bodyHtml   = rows.map(r =>
    '<tr>' + columns.map(c => `<td>${c.value(r) ?? ''}</td>`).join('') + '</tr>'
  ).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
    h2   { margin-bottom: 4px; }
    .sub { color: #666; margin-bottom: 12px; font-size: 10px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: left; }
    th { background: #f0f0f0; font-weight: bold; }
    tr:nth-child(even) { background: #fafafa; }
    @media print { button { display: none; } }
  </style>
</head>
<body>
  <h2>${title}</h2>
  <div class="sub">Period: ${dateFrom} to ${dateTo} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()}</div>
  <table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>
  <script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, icon: Icon }) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2,
                         py: 1.5, '&:last-child': { pb: 1.5 } }}>
        {Icon && <Icon sx={{ color: color || 'primary.main', fontSize: 32 }} />}
        <Box>
          <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Operator attendance detail dialog ─────────────────────────────────────────
function OperatorDetailDialog({ operator, dateFrom, dateTo, open, onClose }) {
  const detectionSettings = useDetectionSettings();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!open || !operator) return;
    setLoading(true); setError('');
    api.get(`/api/v1/reports/operator/${operator.operator_id || operator.id}/attendance`, {
      params: { date_from: dateFrom, date_to: dateTo },
    }).then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || 'Failed'); setLoading(false); });
  }, [open, operator, dateFrom, dateTo]);

  const s = data?.summary;

  const detailColumns = [
    { label: 'Date',         value: r => r.checked_in_at ? new Date(r.checked_in_at).toLocaleDateString() : '' },
    { label: 'Check In',     value: r => r.checked_in_at  ? new Date(r.checked_in_at).toLocaleTimeString()  : '' },
    { label: 'Check Out',    value: r => r.checked_out_at ? new Date(r.checked_out_at).toLocaleTimeString() : '' },
    { label: 'Work (min)',   value: r => r.work_minutes?.toFixed(0) },
    { label: 'Break (min)',  value: r => r.break_minutes?.toFixed(0) },
    { label: 'Drowsy', detectionType: 'drowsiness', value: r => r.drowsiness_count },
    { label: 'Yawns', detectionType: 'yawning', value: r => r.yawn_count },
    { label: 'Status',       value: r => r.status },
  ].filter(c => !c.detectionType || enabled(detectionSettings, c.detectionType));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{operator?.name} · Attendance Detail ({dateFrom} → {dateTo})</span>
        {data?.sessions && (
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<TableChartIcon />}
              onClick={() => downloadExcel(data.sessions, detailColumns,
                `attendance_${operator?.name}_${dateFrom}_${dateTo}`)}>
              Excel
            </Button>
            <Button size="small" startIcon={<PictureAsPdfIcon />} color="error"
              onClick={() => downloadPDF(data.sessions, detailColumns,
                `Attendance — ${operator?.name}`, dateFrom, dateTo)}>
              PDF
            </Button>
          </Stack>
        )}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" mb={2}>{error}</Alert>}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : data && (
          <>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6} sm={3}>
                <StatCard label="Sessions"     value={s.total_sessions}                        icon={PersonIcon}    />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard label="Work Hours"   value={s.total_work_hours.toFixed(1)}  color="#1976d2" icon={AssessmentIcon} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard label="Break Hours"  value={s.total_break_hours.toFixed(1)} color="#ff9800" icon={CoffeeIcon}     />
              </Grid>
              {enabled(detectionSettings, 'drowsiness') && <Grid item xs={6} sm={3}>
                <StatCard label="Drowsy Events" value={s.total_drowsiness}            color="#f44336" icon={WarningIcon}    />
              </Grid>}
            </Grid>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  {detailColumns.map(c => <TableCell key={c.label}>{c.label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.sessions.map(s => (
                  <TableRow key={s.session_id} hover>
                    {detailColumns.map(c => <TableCell key={c.label}>{c.value(s)}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const detectionSettings = useDetectionSettings();
  const tz = useTimezone();
  const [tab,       setTab]       = useState('attendance');
  const [dateFrom,  setDateFrom]  = useState(weekAgo());
  const [dateTo,    setDateTo]    = useState(today());
  const [locFilter, setLocFilter] = useState('');
  const [locations, setLocations] = useState([]);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [detailOp,  setDetailOp]  = useState(null);

  useEffect(() => {
    if (isAdmin()) getLocations().then(setLocations).catch(() => {});
  }, []);

  const run = useCallback(async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (locFilter) params.location_id = locFilter;
      if (tab === 'attendance') {
        const r = await getAttendanceReport(params);
        setData(r.data || []);
      } else if (tab === 'drowsiness') {
        const r = await getDrowsinessReport(params);
        setData(r.data || []);
      } else if (tab === 'presence') {
        const r = await getPresenceSummaries({
          from_date: zonedStartOfDay(dateFrom, tz), to_date: zonedEndOfDay(dateTo, tz),
        });
        setData(r || []);
      } else if (tab === 'shifts') {
        const r = await getShiftUtilizationReport(params);
        setData(r.data || []);
      } else if (tab === 'activity') {
        const r = await getEvents({
          event_source: 'activity', page: 1, limit: 500,
          from_date: zonedStartOfDay(dateFrom, tz), to_date: zonedEndOfDay(dateTo, tz),
          ...(locFilter ? { location_id: locFilter } : {}),
        });
        setData(r.events || []);
      } else {
        const r = await api.get('/api/v1/breaks/stats', { params });
        setData(r.data || []);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Report failed');
    } finally { setLoading(false); }
  }, [tab, dateFrom, dateTo, locFilter, tz]);

  // ── Column definitions per tab ──────────────────────────────────────────────
  const attendanceCols = [
    { label: 'Employee ID', value: r => r.employee_id },
    { label: 'Name',        value: r => r.name },
    { label: 'Sessions',    value: r => r.sessions },
    { label: 'Total Hours', value: r => r.total_hours },
  ];

  const drowsinessCols = [
    { label: 'Employee ID',      value: r => r.employee_id },
    { label: 'Name',             value: r => r.name },
    { label: 'Date',             value: r => r.date },
    { label: 'Drowsy Events', detectionType: 'drowsiness', value: r => r.drowsiness_events },
    { label: 'Yawn Events', detectionType: 'yawning', value: r => r.yawn_events },
    { label: 'Distracted Events', detectionType: 'distracted', value: r => r.distracted_events ?? 0 },
  ].filter(c => !c.detectionType || enabled(detectionSettings, c.detectionType));

  const breaksCols = [
    { label: 'Employee ID',       value: r => r.employee_id },
    { label: 'Name',              value: r => r.name },
    { label: 'Total Breaks',      value: r => r.total_breaks },
    { label: 'Total Mins',        value: r => r.total_break_minutes },
    { label: 'Coffee',  value: r => r.reasons?.coffee  || 0 },
    { label: 'Lunch',   value: r => r.reasons?.lunch   || 0 },
    { label: 'Personal',value: r => r.reasons?.personal || 0 },
    { label: 'Medical', value: r => r.reasons?.medical  || 0 },
    { label: 'Other',   value: r => r.reasons?.other    || 0 },
  ];
  const presenceCols = [
    { label: 'Employee ID', value: r => r.employee_id || '' },
    { label: 'Operator', value: r => r.operator_name || '' },
    { label: `Shift Start (${tz})`, value: r => formatDateTime(r.shift_started_at, tz) },
    { label: 'Timezone', value: r => r.timezone },
    { label: 'Present Hours', value: r => (r.present_seconds / 3600).toFixed(2) },
    { label: 'Away Hours', value: r => (r.away_seconds / 3600).toFixed(2) },
    { label: 'Break Hours', value: r => (r.approved_break_seconds / 3600).toFixed(2) },
    { label: 'Camera Offline Hours', value: r => (r.camera_offline_seconds / 3600).toFixed(2) },
    { label: 'Presence %', value: r => r.presence_percent },
    { label: 'Away Sessions', value: r => r.away_sessions },
    { label: 'Longest Away Minutes', value: r => (r.longest_away_seconds / 60).toFixed(1) },
    { label: 'First Away', value: r => formatDateTime(r.first_away_at, tz) },
    { label: 'Last Away', value: r => formatDateTime(r.last_away_at, tz) },
    { label: 'Corrected', value: r => r.is_corrected ? 'Yes' : 'No' },
    { label: 'Correction Count', value: r => r.correction_count || 0 },
    { label: 'Away Session Count', value: r => r.away_sessions || 0 },
    { label: 'History Available', value: r => r.is_corrected ? 'Yes' : 'No' },
  ];
  const shiftCols = [
    { label: 'Shift', value: r => r.shift_name },
    { label: 'Schedule', value: r => `${r.start_time}–${r.end_time}` },
    { label: 'Timezone', value: r => r.timezone },
    { label: 'Operators', value: r => r.operators },
    { label: 'Present Hours', value: r => (r.present_seconds / 3600).toFixed(2) },
    { label: 'Away Hours', value: r => (r.away_seconds / 3600).toFixed(2) },
    { label: 'Break Hours', value: r => (r.break_seconds / 3600).toFixed(2) },
    { label: 'Presence %', value: r => r.presence_percent },
    { label: 'Late Login', value: r => r.late_logins },
    { label: 'Early Logout', value: r => r.early_logouts },
    { label: 'Corrected Records', value: r => r.corrected_records || 0 },
    { label: 'Total Corrections', value: r => r.total_corrections || 0 },
  ];

  const activityCols = [
    { label: 'Type',         value: r => r.detection_type === 'screenshot' ? 'Screenshot' : 'Browser History' },
    { label: 'Device',       value: r => r.device_name || r.device_id },
    { label: 'Operator',     value: r => r.operator_name || '' },
    { label: `Time (${tz})`, value: r => formatDateTime(r.started_at, tz) },
    { label: 'Capture Mode', value: r => r.capture_mode || '' },
    { label: 'Browsers',     value: r => (r.browsers || []).join(', ') },
    { label: 'Entries',      value: r => r.entry_count ?? '' },
  ];

  const currentCols     = tab === 'attendance' ? attendanceCols
                        : tab === 'drowsiness'  ? drowsinessCols
                        : tab === 'presence' ? presenceCols
                        : tab === 'shifts' ? shiftCols
                        : tab === 'activity' ? activityCols : breaksCols;
  const currentTitle    = tab === 'attendance' ? 'Attendance Report'
                        : tab === 'drowsiness'  ? 'Drowsiness Report'
                        : tab === 'presence' ? 'Presence Monitoring Report'
                        : tab === 'shifts' ? 'Shift Utilization Report'
                        : tab === 'activity' ? 'Work Activity Report' : 'Break Statistics';

  const handleExcel = () => {
    if (!data?.length) return;
    downloadExcel(data, currentCols, `${tab}_${dateFrom}_${dateTo}`);
  };

  const handlePDF = () => {
    if (!data?.length) return;
    downloadPDF(data, currentCols, currentTitle, dateFrom, dateTo);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Reports</Typography>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <Stack direction="row" spacing={1}>
            {['attendance', 'shifts', 'drowsiness',
              ...(enabled(detectionSettings, 'presence') ? ['presence'] : []),
              'breaks',
              ...((enabled(detectionSettings, 'screenshot') || enabled(detectionSettings, 'browser_history')) ? ['activity'] : []),
            ].map(t => (
              <Button key={t} variant={tab === t ? 'contained' : 'outlined'} size="small"
                onClick={() => setTab(t)} sx={{ textTransform: 'capitalize' }}>
                {t}
              </Button>
            ))}
          </Stack>
          <TextField label="From" type="date" size="small" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }} />
          {isAdmin() && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Location</InputLabel>
              <Select value={locFilter} label="Location"
                onChange={e => setLocFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={() => { setDateFrom(weekAgo());  setDateTo(today()); }} size="small">7d</Button>
            <Button onClick={() => { setDateFrom(monthAgo()); setDateTo(today()); }} size="small">30d</Button>
          </Stack>
          <Button variant="contained" onClick={run} startIcon={<AssessmentIcon />}>
            Run
          </Button>
          {data?.length > 0 && (
            <>
              <Button variant="outlined" size="small" startIcon={<TableChartIcon />}
                color="success" onClick={handleExcel}>
                Excel
              </Button>
              <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
                color="error" onClick={handlePDF}>
                PDF
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', mt: 4 }}><CircularProgress /></Box>}

      {/* Attendance */}
      {!loading && data && tab === 'attendance' && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Employee</TableCell>
                <TableCell align="right">Sessions</TableCell>
                <TableCell align="right">Total Hours</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No data for this period.
                </TableCell></TableRow>
              ) : data.map(r => (
                <TableRow key={r.employee_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    <Typography variant="caption" color="text.secondary">#{r.employee_id}</Typography>
                  </TableCell>
                  <TableCell align="right">{r.sessions}</TableCell>
                  <TableCell align="right">{r.total_hours} h</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setDetailOp(r)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Drowsiness */}
      {!loading && data && tab === 'drowsiness' && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Employee</TableCell>
                <TableCell>Date</TableCell>
                {enabled(detectionSettings, 'drowsiness') && <TableCell align="right">😴 Drowsy</TableCell>}
                {enabled(detectionSettings, 'yawning') && <TableCell align="right">🥱 Yawns</TableCell>}
                {enabled(detectionSettings, 'distracted') && <TableCell align="right">👀 Distracted</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No detections in this period.
                </TableCell></TableRow>
              ) : data.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    <Typography variant="caption" color="text.secondary">#{r.employee_id}</Typography>
                  </TableCell>
                  <TableCell>{r.date}</TableCell>
                  {enabled(detectionSettings, 'drowsiness') && <TableCell align="right">
                    <Chip label={r.drowsiness_events} size="small"
                      color={r.drowsiness_events > 10 ? 'error' : r.drowsiness_events > 0 ? 'warning' : 'default'}
                      variant="outlined" />
                  </TableCell>}
                  {enabled(detectionSettings, 'yawning') && <TableCell align="right">
                    <Chip label={r.yawn_events} size="small" variant="outlined" />
                  </TableCell>}
                  {enabled(detectionSettings, 'distracted') && <TableCell align="right">
                    <Chip label={r.distracted_events ?? 0} size="small" variant="outlined"
                      sx={{ borderColor: '#9c27b0', color: '#9c27b0' }} />
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Breaks */}
      {!loading && data && tab === 'presence' && (
        <Paper><Table size="small"><TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>
          {presenceCols.map(c => <TableCell key={c.label}>{c.label}</TableCell>)}
        </TableRow></TableHead><TableBody>
          {data.map(r => <TableRow key={r.id} hover>
            {presenceCols.map(c => <TableCell key={c.label}>{c.value(r)}</TableCell>)}
          </TableRow>)}
        </TableBody></Table></Paper>
      )}

      {/* Shift utilization */}
      {!loading && data && tab === 'shifts' && (
        <Paper><Table size="small"><TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>
          {shiftCols.map(c => <TableCell key={c.label}>{c.label}</TableCell>)}
        </TableRow></TableHead><TableBody>
          {data.length ? data.map(r => <TableRow key={r.shift_id} hover>
            {shiftCols.map(c => <TableCell key={c.label}>{c.value(r)}</TableCell>)}
          </TableRow>) : <TableRow><TableCell colSpan={shiftCols.length} align="center" sx={{ py: 4 }}>
            No shift data for this period.
          </TableCell></TableRow>}
        </TableBody></Table></Paper>
      )}

      {/* Work Activity (Screenshot + Browser History events) */}
      {!loading && data && tab === 'activity' && (
        <Paper><Table size="small"><TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>
          {activityCols.map(c => <TableCell key={c.label}>{c.label}</TableCell>)}
        </TableRow></TableHead><TableBody>
          {data.length ? data.map(r => <TableRow key={r.id} hover>
            {activityCols.map(c => <TableCell key={c.label}>{c.value(r)}</TableCell>)}
          </TableRow>) : <TableRow><TableCell colSpan={activityCols.length} align="center" sx={{ py: 4 }}>
            No screenshot or browser history events for this period.
          </TableCell></TableRow>}
        </TableBody></Table></Paper>
      )}

      {/* Breaks */}
      {!loading && data && tab === 'breaks' && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Employee</TableCell>
                <TableCell align="right">Total Breaks</TableCell>
                <TableCell align="right">Total Mins</TableCell>
                <TableCell>Reasons</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No break data.
                </TableCell></TableRow>
              ) : data.map(r => (
                <TableRow key={r.operator_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    <Typography variant="caption" color="text.secondary">#{r.employee_id}</Typography>
                  </TableCell>
                  <TableCell align="right">{r.total_breaks}</TableCell>
                  <TableCell align="right"
                    sx={{ color: r.total_break_minutes > 60 ? 'warning.main' : 'inherit' }}>
                    {r.total_break_minutes} min
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {Object.entries(r.reasons || {}).map(([k, v]) => (
                        <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <OperatorDetailDialog
        open={!!detailOp} operator={detailOp}
        dateFrom={dateFrom} dateTo={dateTo}
        onClose={() => setDetailOp(null)} />
    </Box>
  );
}
